'use client';

import { Button } from '@shared/components/shadcn';
import { useStickyVisibility } from '@shared/hooks';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRef } from 'react';
import { useLotteryNextDraw } from '../../../lotteries/hooks';
import { JackpotLabel } from '../../../lotteries/JackpotLabel/JackpotLabel';
import { LotteryPrice } from '../../../lotteries/LotteryPrice/LotteryPrice';
import { LotteryHeaderContainer } from '../../../LotteryHeaderContainer/LotteryHeaderContainer';
import { Loading } from './components/Loading';
import { NextDrawSticky } from './components/NextDrawSticky/NextDrawSticky';
import type { NextDrawProps } from './types';

function NextDraw({ className, lotteryName, betUrl, currency, disableSticky, icon: Icon, ...props }: NextDrawProps) {
  const t = useTranslations('lotteries.results.next_draw');
  const { data, isLoading } = useLotteryNextDraw(lotteryName);
  const nextDraw = data?.find(draw => draw.lotteryName === lotteryName);

  const nextDrawRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useStickyVisibility({ targetRef: nextDrawRef, disabled: disableSticky });
  const containerClass = twMerge('rounded-lg p-8 h-auto flex', className);

  if (isLoading) return <Loading className={containerClass} lotteryName={lotteryName} />;
  if (!nextDraw) return null;

  const jackpot = nextDraw?.jackpot.regular[currency];
  const tTitle = t('title', { date: new Date(nextDraw.drawingDate) }),
    tPlaceBet = t('place_bet');

  return (
    <LotteryHeaderContainer {...props} lotteryName={lotteryName} className={containerClass}>
      <div ref={nextDrawRef} className="flex flex-col gap-y-5 flex-1">
        <div className="flex flex-col">
          <h5 className="-mb-1">{tTitle}</h5>
          <JackpotLabel namespace={`lotteries.${lotteryName}`} jackpot={jackpot!} jackpotLabelSize="medium" className="md:display-l-strong" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-x-4 items-center">
            <Button asChild variant="primary" size="m" className="light" data-track-name="Place Bet">
              <Link href={betUrl}>{tPlaceBet}</Link>
            </Button>
            <LotteryPrice price={nextDraw?.price[currency] ?? 0} priceVariant="label" />
          </div>
          {Icon ? <Icon aria-hidden className="pointer-events-none !size-[78px]" data-testid="next-draw-logo" /> : null}
        </div>
      </div>
      {isVisible && !disableSticky ? (
        <NextDrawSticky className="md:hidden" lotteryName={lotteryName} jackpotText={tTitle} jackpot={jackpot!} betUrl={betUrl} placeBetText={tPlaceBet} />
      ) : null}
    </LotteryHeaderContainer>
  );
}

export { NextDraw };
